<script lang="ts">
  import { faExpand, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons'
  import type { Map as MapLibreMap, Marker } from 'maplibre-gl'
  import { mount, onMount, unmount } from 'svelte'
  import type { CategoryMeta } from '../categories'
  import { getI18n } from '../i18n'
  import type { SerializedPlace } from '../types'
  import type { Coordinate } from '../utils/gpx'
  import { loadMapLibrary, type MapLibrary } from '../utils/mapLibrary'
  import { MAP_BOUNDS, buildMapStyle, resolveCssColor } from '../utils/mapStyle'
  import { formatNumber } from '../utils/route'
  import Icon from './Icon.svelte'
  import MapMarker from './MapMarker.svelte'

  const MIN_ZOOM = 10
  const MAX_ZOOM = 18
  const PLACE_ZOOM = 16
  // Zoom animations can end a hair below the limit.
  const ZOOM_TOLERANCE = 0.01
  // Room for the zoom buttons and the number tags below the markers.
  const PADDING = { top: 48, bottom: 56, left: 56, right: 56 }

  // `numbered` labels the markers 1, 2, 3 … like the list next to the map. `route` is drawn as a line under the markers.
  let {
    places,
    variant,
    numbered = false,
    route = null,
    selectedIndex = $bindable(null),
  }: {
    places: SerializedPlace[]
    variant: CategoryMeta['variant']
    numbered?: boolean
    route?: Coordinate[] | null
    selectedIndex?: number | null
  } = $props()

  const i18n = getI18n()

  let element: HTMLDivElement
  let canvas: HTMLDivElement
  let map: MapLibreMap | null = null
  let markers: { marker: Marker; props: { active: boolean }; component: ReturnType<typeof mount> }[] = []
  let status = $state<'idle' | 'loading' | 'ready' | 'failed'>('idle')
  let zoom = $state(PLACE_ZOOM)

  onMount(() => {
    let destroyed = false
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        void start(() => destroyed)
      },
      { rootMargin: '300px 0px' },
    )
    observer.observe(element)

    return () => {
      destroyed = true
      observer.disconnect()
      markers.forEach(({ marker, component }) => {
        void unmount(component)
        marker.remove()
      })
      map?.remove()
    }
  })

  async function start(isDestroyed: () => boolean) {
    status = 'loading'
    let library: MapLibrary
    try {
      library = await loadMapLibrary()
    } catch (error) {
      console.error(error)
      status = 'failed'
      return
    }
    if (isDestroyed()) return

    const { maplibre, basemaps } = library
    const createdMap = new maplibre.Map({
      container: canvas,
      style: buildMapStyle(basemaps, mapColors(), window.location.origin),
      ...(places.length === 1 ? { center: position(places[0]), zoom: PLACE_ZOOM } : { bounds: overviewBounds(), fitBoundsOptions: { padding: PADDING, maxZoom: PLACE_ZOOM } }),
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      maxBounds: MAP_BOUNDS,
      cooperativeGestures: true,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      locale: {
        'CooperativeGesturesHandler.WindowsHelpText': i18n.t('map.gestures.windows'),
        'CooperativeGesturesHandler.MacHelpText': i18n.t('map.gestures.mac'),
        'CooperativeGesturesHandler.MobileHelpText': i18n.t('map.gestures.mobile'),
        'AttributionControl.ToggleAttribution': i18n.t('attribution.toggle'),
      },
    })
    createdMap.touchZoomRotate.disableRotation()
    createdMap.keyboard.disableRotation()
    // MapLibre only knows physical corners, so the attribution follows the reading direction by hand.
    const rightToLeft = getComputedStyle(element).direction === 'rtl'
    createdMap.addControl(new maplibre.AttributionControl({ compact: true }), rightToLeft ? 'bottom-left' : 'bottom-right')
    createdMap.on('zoom', () => (zoom = createdMap.getZoom()))
    createdMap.once('load', () => {
      zoom = createdMap.getZoom()
      status = 'ready'
    })
    map = createdMap

    markers = places.map((place, index) => {
      const target = document.createElement('div')
      const props = $state({
        icon: place.icon,
        variant,
        number: numbered ? formatNumber(index + 1, i18n.locale) : null,
        active: index === selectedIndex,
        label: markerLabel(place, index),
        // A single place has no list to sync with, so its marker is not a button.
        onclick: numbered ? () => showPlace(index) : null,
      })
      const component = mount(MapMarker, { target, props })
      const marker = new maplibre.Marker({ element: target, anchor: 'center' }).setLngLat(position(place)).addTo(createdMap)

      return { marker, props, component }
    })
  }

  /** Selects a place and moves the map to it, also when it was selected already but the map has moved away since. */
  export function showPlace(index: number) {
    selectedIndex = index
    map?.easeTo({ center: position(places[index]), zoom: Math.max(map.getZoom(), PLACE_ZOOM) })
  }

  $effect(() => {
    const index = selectedIndex
    if (status !== 'ready') return
    markers.forEach(({ props }, markerIndex) => (props.active = markerIndex === index))
  })

  // The route usually arrives after the map, as its GPX file is downloaded separately.
  $effect(() => {
    if (status !== 'ready' || !map || !route || route.length < 2 || map.getSource('route')) return

    map.addSource('route', { type: 'geojson', data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: route } } })
    map.addLayer({
      id: 'route-casing',
      type: 'line',
      source: 'route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': resolveCssColor(element, '--color-bg'), 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 6, 17, 10] },
    })
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': resolveCssColor(element, '--map-view-accent'), 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 17, 6] },
    })
    if (selectedIndex === null) showAll()
  })

  function showAll() {
    if (!map) return
    if (places.length === 1) {
      map.easeTo({ center: position(places[0]), zoom: PLACE_ZOOM })
    } else {
      map.fitBounds(overviewBounds(), { padding: PADDING, maxZoom: PLACE_ZOOM })
    }
  }

  function overviewBounds(): [Coordinate, Coordinate] {
    const coordinates = [...places.map(position), ...(route ?? [])]
    const longitudes = coordinates.map(([longitude]) => longitude)
    const latitudes = coordinates.map(([, latitude]) => latitude)

    return [
      [Math.min(...longitudes), Math.min(...latitudes)],
      [Math.max(...longitudes), Math.max(...latitudes)],
    ]
  }

  function position(place: SerializedPlace): Coordinate {
    return [place.longitude, place.latitude]
  }

  function markerLabel(place: SerializedPlace, index: number): string {
    const icon = i18n.t(`mapIcons.${place.icon}`)
    return numbered
      ? i18n.t('map.numberedMarker', { number: formatNumber(index + 1, i18n.locale), name: place.name, icon })
      : i18n.t('map.marker', { name: place.name, icon })
  }

  function mapColors() {
    return {
      land: resolveCssColor(element, '--color-bg-muted'),
      water: resolveCssColor(element, '--color-blue-90'),
      park: resolveCssColor(element, '--color-green-90'),
      parkMuted: resolveCssColor(element, '--color-green-95'),
      buildings: resolveCssColor(element, '--color-gray-90'),
      label: resolveCssColor(element, '--color-text-muted'),
    }
  }
</script>

<div
  class={['map-view', {
    '-variant-brand': variant === 'brand',
    '-variant-warning': variant === 'warning',
    '-variant-success': variant === 'success',
  }]}
  bind:this={element}
>
  <div class="map-view-canvas" bind:this={canvas}></div>
  {#if status === 'loading'}
    <p class="map-view-status">{i18n.t('map.loading')}</p>
  {:else if status === 'failed'}
    <p class="map-view-status">{i18n.t('map.failed')}</p>
  {/if}
  {#if status === 'ready'}
    <div class="map-view-zoom">
      <button type="button" class="map-view-zoom-button" aria-label={i18n.t('map.zoomIn')} disabled={zoom >= MAX_ZOOM - ZOOM_TOLERANCE} onclick={() => map?.zoomIn()}>
        <Icon icon={faPlus} />
      </button>
      <button type="button" class="map-view-zoom-button" aria-label={i18n.t('map.zoomOut')} disabled={zoom <= MIN_ZOOM + ZOOM_TOLERANCE} onclick={() => map?.zoomOut()}>
        <Icon icon={faMinus} />
      </button>
      <button type="button" class="map-view-zoom-button" aria-label={i18n.t('map.showAll')} onclick={showAll}>
        <Icon icon={faExpand} />
      </button>
    </div>
  {/if}
</div>

<style>
  .map-view {
    position: relative;
    height: 100%;
    background-color: var(--color-bg-muted);
  }

  .map-view.-variant-brand {
    --map-view-accent: var(--color-variant-brand);
  }

  .map-view.-variant-warning {
    --map-view-accent: var(--color-variant-warning);
  }

  .map-view.-variant-success {
    --map-view-accent: var(--color-variant-success);
  }

  .map-view-canvas {
    position: absolute;
    inset: 0;
  }

  .map-view-status {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    margin: 0;
    padding: var(--space-7);
    font-size: var(--font-size-4);
    color: var(--color-text-muted);
    text-align: center;
  }

  .map-view-zoom {
    position: absolute;
    inset-block-start: var(--space-5);
    inset-inline-end: var(--space-5);
    z-index: 2;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: var(--border-style-2);
    border-radius: var(--radius-2);
    background-color: var(--color-bg);
    box-shadow: var(--box-shadow-1);
  }

  .map-view-zoom-button {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-text);
    font-size: var(--font-size-4);
    cursor: pointer;
  }

  .map-view-zoom-button + .map-view-zoom-button {
    border-block-start: var(--border-style-2);
  }

  .map-view-zoom-button:hover:not(:disabled) {
    background-color: var(--color-bg-muted);
  }

  .map-view-zoom-button:disabled {
    cursor: default;
    opacity: var(--opacity-2);
  }

  .map-view-zoom-button:focus-visible {
    outline: var(--border-2) solid var(--map-view-accent);
    outline-offset: calc(-1 * var(--border-2));
  }
</style>
